import React, { useState } from 'react';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import styles from './ImportPreviewModal.module.scss';

function Section({
  label,
  count,
  dotClass,
  badgeClass,
  tagClass,
  tagText,
  getTagText,
  items,
  keyField,
  emptyNote,
  defaultOpen,
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={() => setOpen((v) => !v)}>
        <div className={styles.sectionLeft}>
          <span className={`${styles.sectionDot} ${dotClass}`} />
          <span className={styles.sectionLabel}>{label}</span>
          <span className={`${styles.countBadge} ${badgeClass}`}>{count}</span>
        </div>
        <span className={`${styles.chevron} ${open ? styles.open : ''}`}>
          ▼
        </span>
      </div>

      {open && (
        <div className={styles.sectionBody}>
          {items.length === 0 ? (
            <div className={styles.emptySectionNote}>{emptyNote}</div>
          ) : (
            items.map((item) => (
              <div
                key={
                  item[keyField] ?? item.externalId ?? item.email ?? item.name
                }
                className={styles.employeeRow}
              >
                <div className={styles.empInfo}>
                  <span className={styles.empName}>
                    {item.name || '(No name)'}
                  </span>
                  {item.email && (
                    <span className={styles.empEmail}>{item.email}</span>
                  )}
                  {item.reason && (
                    <span className={styles.empReason}>{item.reason}</span>
                  )}
                </div>
                <span className={`${styles.empTag} ${tagClass}`}>
                  {getTagText ? getTagText(item) : tagText}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export const ImportPreviewModal = ({
  isOpen,
  onClose,
  onConfirm,
  preview,
  loading,
  confirming,
  source,
  results,
  resultsTitle,
}) => {
  if (!isOpen) return null;

  const isResultsMode = results != null;

  if (isResultsMode) {
    const employeeResults = results.employeeResults || [];
    const createdItems = employeeResults.filter(
      (e) => e.outcome === 'imported'
    );
    const invitedItems = employeeResults.filter((e) => e.outcome === 'invited');
    const linkedItems = employeeResults.filter((e) => e.outcome === 'mapped');
    const skippedItems = employeeResults.filter((e) => e.outcome === 'skipped');
    const failedItems = employeeResults.filter((e) => e.outcome === 'error');

    const totalActioned =
      (results.imported || 0) + (results.invited || 0) + (results.mapped || 0);
    const importStatus =
      (results.errors || 0) === 0
        ? 'completed'
        : totalActioned > 0
          ? 'partial'
          : 'failed';

    const bannerClass =
      importStatus === 'completed'
        ? styles.bannerGreen
        : importStatus === 'partial'
          ? styles.bannerAmber
          : styles.bannerRed;
    const bannerText =
      importStatus === 'completed'
        ? 'Import completed successfully'
        : importStatus === 'partial'
          ? `Completed with ${results.errors} error${results.errors !== 1 ? 's' : ''}`
          : 'Import failed — no employees were processed';

    return (
      <div
        className={styles.overlay}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2>{resultsTitle || `Import Results · ${source}`}</h2>
            <button className={styles.closeBtn} onClick={onClose}>
              ✕
            </button>
          </div>

          <div className={styles.body}>
            {loading ? (
              <div className={styles.loadingState}>
                <div className={styles.spinner} />
                Loading details…
              </div>
            ) : (
              <>
                <div className={`${styles.resultsBanner} ${bannerClass}`}>
                  {bannerText}
                </div>
                {createdItems.length > 0 && (
                  <Section
                    label="Accounts Created"
                    count={createdItems.length}
                    dotClass={styles.dotGreen}
                    badgeClass={styles.badgeGreen}
                    tagClass={styles.tagNew}
                    tagText="Created"
                    items={createdItems}
                    keyField="externalId"
                    emptyNote=""
                    defaultOpen
                  />
                )}
                {invitedItems.length > 0 && (
                  <Section
                    label="Invites Sent"
                    count={invitedItems.length}
                    dotClass={styles.dotGreen}
                    badgeClass={styles.badgeGreen}
                    tagClass={styles.tagNew}
                    tagText="Invited"
                    items={invitedItems}
                    keyField="externalId"
                    emptyNote=""
                    defaultOpen
                  />
                )}
                {linkedItems.length > 0 && (
                  <Section
                    label="Linked to Existing"
                    count={linkedItems.length}
                    dotClass={styles.dotGray}
                    badgeClass={styles.badgeGray}
                    tagClass={styles.tagMapped}
                    tagText="Linked"
                    items={linkedItems}
                    keyField="externalId"
                    emptyNote=""
                    defaultOpen={false}
                  />
                )}
                {skippedItems.length > 0 && (
                  <Section
                    label="Already Mapped"
                    count={skippedItems.length}
                    dotClass={styles.dotGray}
                    badgeClass={styles.badgeGray}
                    tagClass={styles.tagMapped}
                    tagText="Skipped"
                    items={skippedItems}
                    keyField="externalId"
                    emptyNote=""
                    defaultOpen={false}
                  />
                )}
                {failedItems.length > 0 && (
                  <Section
                    label="Failed"
                    count={failedItems.length}
                    dotClass={styles.dotRed}
                    badgeClass={styles.badgeRed}
                    tagClass={styles.tagNoEmail}
                    tagText="Error"
                    items={failedItems}
                    keyField="externalId"
                    emptyNote=""
                    defaultOpen
                  />
                )}
                {employeeResults.length === 0 && (
                  <div
                    className={styles.emptySectionNote}
                    style={{ padding: '24px', textAlign: 'center' }}
                  >
                    No employee details available.
                  </div>
                )}
              </>
            )}
          </div>

          <div className={styles.footer}>
            <Button
              text="Close"
              variant={buttonVariants.secondary}
              size={buttonSizes.sm}
              onClick={onClose}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Preview mode ───────────────────────────────────────────────────────────
  const newCount = preview?.newEmployees?.length ?? 0;
  const elsewhereCount = preview?.existingElsewhere?.length ?? 0;
  const mappedCount = preview?.alreadyMapped?.length ?? 0;
  const noEmailCount = preview?.noEmail?.length ?? 0;
  const toImport = newCount + elsewhereCount;

  return (
    <div
      className={styles.overlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>Import Preview · {source}</h2>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            disabled={confirming}
          >
            ✕
          </button>
        </div>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              Fetching employees…
            </div>
          ) : (
            <>
              <Section
                label="New to Prodaff"
                count={newCount}
                dotClass={styles.dotGreen}
                badgeClass={styles.badgeGreen}
                tagClass={styles.tagNew}
                tagText="Will be invited"
                items={preview?.newEmployees ?? []}
                keyField="email"
                emptyNote="No new employees to invite."
                defaultOpen={newCount > 0}
              />
              <Section
                label="Already in Prodaff — different company"
                count={elsewhereCount}
                dotClass={styles.dotAmber}
                badgeClass={styles.badgeAmber}
                tagClass={styles.tagElsewhere}
                tagText="Other company"
                items={preview?.existingElsewhere ?? []}
                keyField="email"
                emptyNote="No cross-company matches found."
                defaultOpen={elsewhereCount > 0}
              />
              <Section
                label="Already mapped / in this company"
                count={mappedCount}
                dotClass={styles.dotGray}
                badgeClass={styles.badgeGray}
                tagClass={styles.tagMapped}
                tagText="Will be skipped"
                items={preview?.alreadyMapped ?? []}
                keyField={
                  preview?.alreadyMapped?.[0]?.qbId !== undefined
                    ? 'qbId'
                    : 'sfId'
                }
                emptyNote="No already-mapped employees."
                defaultOpen={false}
              />
              <Section
                label="No email address"
                count={noEmailCount}
                dotClass={styles.dotRed}
                badgeClass={styles.badgeRed}
                tagClass={styles.tagNoEmail}
                tagText="Cannot import"
                items={preview?.noEmail ?? []}
                keyField={
                  preview?.noEmail?.[0]?.qbId !== undefined ? 'qbId' : 'sfId'
                }
                emptyNote="All employees have email addresses."
                defaultOpen={noEmailCount > 0}
              />
            </>
          )}
        </div>

        <div className={styles.footer}>
          {!loading && (
            <span className={styles.summary}>
              <span className={styles.summaryBold}>{toImport}</span> employee
              {toImport !== 1 ? 's' : ''} will be processed
            </span>
          )}
          <Button
            text="Cancel"
            variant={buttonVariants.secondary}
            size={buttonSizes.sm}
            onClick={onClose}
            disabled={confirming}
          />
          <Button
            text={confirming ? 'Importing…' : 'Confirm & Import'}
            variant={buttonVariants.primary}
            size={buttonSizes.sm}
            onClick={onConfirm}
            disabled={loading || confirming || (!loading && toImport === 0)}
          />
        </div>
      </div>
    </div>
  );
};
